import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  adjustCustomerLoyaltyPoints,
  adjustCustomerStoreCredit,
  getCustomerLoyaltyPointsLedger,
  getCustomerStoreCreditLedger,
} from '@/lib/api/customers.js';
import { formatCurrency, formatDateTime, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function CollapsibleSection({
  title,
  summary,
  children,
  defaultOpen = false,
}: {
  title: string;
  summary?: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-slate-100">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-5 py-3 text-left text-sm font-semibold text-slate-800 hover:bg-slate-50"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {title}
        {summary ? <span className="ml-auto text-xs font-normal text-slate-500">{summary}</span> : null}
      </button>
      {open ? <div className="px-5 pb-4">{children}</div> : null}
    </div>
  );
}

function AdjustForm({
  kind,
  onSubmit,
  busy,
}: {
  kind: 'credit' | 'points';
  onSubmit: (payload: { signedAmount: number; reason: string; notes: string }) => void;
  busy: boolean;
}) {
  const [mode, setMode] = useState<'add' | 'subtract'>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const unit = kind === 'credit' ? 'dollars' : 'points';
  const parsed = Number(amount);
  const validAmount =
    amount.trim() !== '' && Number.isFinite(parsed) && parsed > 0 && (kind === 'credit' || Number.isInteger(parsed));

  return (
    <form
      className="mt-3 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!validAmount || !reason.trim()) return;
        const signed = mode === 'add' ? parsed : -parsed;
        onSubmit({ signedAmount: signed, reason: reason.trim(), notes: notes.trim() });
        setAmount('');
        setReason('');
        setNotes('');
        setMode('add');
      }}
    >
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
            mode === 'add' ? 'bg-primary text-white' : 'border border-slate-300 bg-white text-slate-700'
          }`}
          onClick={() => setMode('add')}
        >
          Add
        </button>
        <button
          type="button"
          className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
            mode === 'subtract' ? 'bg-primary text-white' : 'border border-slate-300 bg-white text-slate-700'
          }`}
          onClick={() => setMode('subtract')}
        >
          Subtract
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Amount ({unit})
          </span>
          <input
            type="number"
            min={kind === 'credit' ? '0.01' : '1'}
            step={kind === 'credit' ? '0.01' : '1'}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </label>
        <label className="block text-sm sm:col-span-1">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Reason <span className="text-rose-600">*</span>
          </span>
          <input
            type="text"
            maxLength={300}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Required for audit"
            required
          />
        </label>
      </div>
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Notes</span>
        <input
          type="text"
          maxLength={kind === 'credit' ? 500 : 300}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional detail"
        />
      </label>
      <button
        type="submit"
        disabled={busy || !validAmount || !reason.trim()}
        className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
      >
        {busy ? 'Saving…' : mode === 'add' ? `Add ${kind === 'credit' ? 'credit' : 'points'}` : `Subtract ${kind === 'credit' ? 'credit' : 'points'}`}
      </button>
    </form>
  );
}

export function CustomerCreditPointsSection({
  customerId,
  pointsBalance,
  storeCreditBalance,
  canAdjust,
}: {
  customerId: string;
  pointsBalance: number;
  storeCreditBalance: string | number | null | undefined;
  canAdjust: boolean;
}) {
  const queryClient = useQueryClient();
  const [historyOpen, setHistoryOpen] = useState(false);

  const creditLedgerQuery = useQuery({
    queryKey: ['customer-store-credit-ledger', customerId],
    queryFn: () => getCustomerStoreCreditLedger(customerId),
    enabled: historyOpen,
  });

  const pointsLedgerQuery = useQuery({
    queryKey: ['customer-loyalty-points-ledger', customerId],
    queryFn: () => getCustomerLoyaltyPointsLedger(customerId),
    enabled: historyOpen,
  });

  const creditMutation = useMutation({
    mutationFn: (body: { amount: number; reason: string; notes?: string | null }) =>
      adjustCustomerStoreCredit(customerId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['customer-store-credit-ledger', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      toastSuccess('Store credit updated');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const pointsMutation = useMutation({
    mutationFn: (body: { delta: number; reason: string; notes?: string | null }) =>
      adjustCustomerLoyaltyPoints(customerId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['customer-loyalty-points-ledger', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      toastSuccess('Loyalty points updated');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const creditRows = creditLedgerQuery.data ?? [];
  const pointsRows = pointsLedgerQuery.data ?? [];

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <h3 className="text-lg font-semibold text-slate-950">Store Credit & Loyalty Points</h3>
        <p className="text-sm text-slate-500">
          Current balances{canAdjust ? ' — managers can add or subtract with an audit reason' : ' — view only'}
        </p>
      </div>

      <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Store credit</div>
          <div className="mt-1 text-2xl font-bold text-slate-950">
            {formatCurrency(storeCreditBalance ?? '0')}
          </div>
          {canAdjust ? (
            <AdjustForm
              kind="credit"
              busy={creditMutation.isPending}
              onSubmit={({ signedAmount, reason, notes }) =>
                creditMutation.mutate({
                  amount: signedAmount,
                  reason,
                  notes: notes || null,
                })
              }
            />
          ) : (
            <p className="mt-2 text-xs text-slate-500">Manager role required to adjust credit</p>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Loyalty points</div>
          <div className="mt-1 text-2xl font-bold text-slate-950">{pointsBalance}</div>
          {canAdjust ? (
            <AdjustForm
              kind="points"
              busy={pointsMutation.isPending}
              onSubmit={({ signedAmount, reason, notes }) =>
                pointsMutation.mutate({
                  delta: signedAmount,
                  reason,
                  notes: notes || null,
                })
              }
            />
          ) : (
            <p className="mt-2 text-xs text-slate-500">Manager role required to adjust points</p>
          )}
        </div>
      </div>

      <div className="border-t border-slate-100">
        <button
          type="button"
          className="flex w-full items-center gap-2 px-5 py-3 text-left text-sm font-semibold text-slate-800 hover:bg-slate-50"
          onClick={() => setHistoryOpen((o) => !o)}
        >
          {historyOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Credit & points history
          <span className="ml-auto text-xs font-normal text-slate-500">Recent ledger entries</span>
        </button>
        {historyOpen ? (
          <div className="space-y-2 px-0 pb-2">
            <CollapsibleSection
              title="Store credit ledger"
              summary={`${creditRows.length} shown`}
              defaultOpen
            >
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Amount</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Reason</th>
                      <th className="px-3 py-2">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {creditRows.map((row) => (
                      <tr key={row.id}>
                        <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                          {formatDateTime(row.createdAt)}
                        </td>
                        <td
                          className={`whitespace-nowrap px-3 py-2 font-semibold ${
                            Number(row.amount) < 0 ? 'text-rose-700' : 'text-emerald-700'
                          }`}
                        >
                          {formatCurrency(row.amount)}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{prettyLabel(row.type)}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {row.reason || row.notes || '—'}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{row.actorName ?? 'System'}</td>
                      </tr>
                    ))}
                    {!creditLedgerQuery.isLoading && creditRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                          No store credit history yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Loyalty points ledger"
              summary={`${pointsRows.length} shown`}
              defaultOpen
            >
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Delta</th>
                      <th className="px-3 py-2">Source</th>
                      <th className="px-3 py-2">Reason / notes</th>
                      <th className="px-3 py-2">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pointsRows.map((row) => (
                      <tr key={row.id}>
                        <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                          {formatDateTime(row.createdAt)}
                        </td>
                        <td
                          className={`whitespace-nowrap px-3 py-2 font-semibold ${
                            row.delta < 0 ? 'text-rose-700' : 'text-emerald-700'
                          }`}
                        >
                          {row.delta > 0 ? `+${row.delta}` : row.delta}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{prettyLabel(row.sourceType)}</td>
                        <td className="px-3 py-2 text-slate-600">{row.notes || '—'}</td>
                        <td className="px-3 py-2 text-slate-600">{row.actorName ?? 'System'}</td>
                      </tr>
                    ))}
                    {!pointsLedgerQuery.isLoading && pointsRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                          No loyalty points history yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          </div>
        ) : null}
      </div>
    </section>
  );
}
